<template>
  <div
    class="flex flex-col justify-between flex-grow gap-2"
    :style="{ height: 'calc(100%)' }"
  >
    <div class="relative flex flex-col" :style="{ height: 'calc(100%)' }">
      <!-- ratio boxes -->
      <div
        v-if="!isCollapsed"
        :id="`sbt-ratio-${measurement.id}-${JSON.stringify(currentTrial)}`"
        class="flex flex-wrap items-center justify-center gap-1 pb-2"
      >
        <div
          v-for="taskCode in ratioScores"
          :key="taskCode.id"
          class="relative flex flex-col-reverse items-center overflow-hidden transition-all duration-300 border rounded"
          :class="{
            'w-7.5 h-7.5': sessionStatus === 'draft',
            'w-10 h-10': sessionStatus !== 'draft',
            'bg-slate-200 border-slate-200': taskCode.count <= 0,
            'bg-teal-100 border-teal-100': taskCode.count >= 1,
            'bg-teal-100 border-teal-700':
              taskCode.id === currentTrial.target_task_id
          }"
        >
          <div v-for="(ratio, idx) in taskCode.ratios" :key="idx">
            <div
              v-if="ratio.prompt_id"
              class="relative w-10 bg-teal-300"
              :style="{
                height: 2.5 / taskCode.max_ratio + 'rem'
              }"
            >
              <div
                v-if="ratio.target_problem_behavior_id"
                class="absolute top-0 right-0 h-full bg-tomato-600"
                :style="{ width: '0.3125rem' }"
              ></div>
            </div>
          </div>
          <div
            class="absolute left-0 w-full font-bold text-center"
            :class="{
              'text-slate-600': taskCode.count === 0,
              'text-teal-700':
                taskCode.count > 0 ||
                taskCode.id === currentTrial.target_task_id
            }"
            :style="{
              fontSize: sessionStatus === 'draft' ? '0.5rem' : '0.625rem',
              top: '50%',
              transform: 'translateY(-50%)'
            }"
          >
            {{ isOpenTrialHistory ? `${taskCode.average}%` : taskCode.code }}
          </div>
        </div>
      </div>

      <!-- select a value for new or edit trial -->
      <div
        v-if="!isOpenTrialHistory || isOpenEditTrial"
        class="flex flex-col h-full"
      >
        <!-- header information for active trial -->
        <div class="flex items-center justify-between flex-shrink-0 h-9">
          <div class="flex items-center gap-1">
            <div>
              <BlTextBody2 text="Current" class="text-slate-700" />
            </div>
            <div>
              <BlTextSubtitle3
                :text="currentTrialData.task_code.code"
                class="text-teal-800"
              />
            </div>
            <div v-if="display === 'select-next-task' && !isOpenEditTrial">
              <BlTextSubtitle3
                :text="`| ${currentTrialData.average}%`"
                class="text-teal-800"
              />
            </div>
            <BlIcon
              v-if="display === 'select-prompt' && !isOpenEditTrial"
              icon="edit"
              color="teal-7"
              clickable
              class="w-4 h-4"
              @clicked="onUndoTrial"
            />
          </div>
          <div class="flex items-center gap-1">
            <BlTextBody2 text="Trial" class="text-slate-700" />
            <BlTextSubtitle3 :text="currentTrial.key" class="text-teal-800" />
          </div>
        </div>

        <div v-if="!isOpenProblemBehavior" class="h-full">
          <!-- select a prompt -->
          <div
            v-if="display === 'select-prompt'"
            class="flex flex-col items-center content-center justify-center h-full gap-2"
          >
            <div
              :id="`sbt-scroll-${measurement.id}`"
              class="flex w-full gap-4 pb-4 overflow-x-auto scrolling-touch snap-x-mandatory scrollbar-lg"
              :class="{
                'max-w-56': sessionStatus === 'draft',
                'max-w-72': sessionStatus !== 'draft'
              }"
              dir="ltr"
              @scroll="onScroll"
            >
              <div
                v-for="(promptBoxes, idx) in promptBoxesPages"
                :key="`${measurement.id}-sbt-prompt-boxes-${idx + 1}`"
                :id="`${measurement.id}-sbt-prompt-boxes-${idx + 1}`"
                class="flex justify-center flex-shrink-0 w-full snap-center"
              >
                <div
                  class="flex flex-wrap items-start content-center justify-center w-full gap-x-2 gap-y-3"
                  :class="{ '-translate-y-1 scale-75': isCollapsed }"
                >
                  <div
                    v-for="(prompt, promptIdx) in promptBoxes"
                    :key="promptIdx"
                  >
                    <div
                      class="relative flex items-center justify-center flex-shrink-0 cursor-pointer hover-darker"
                      :class="{
                        'h-12 w-12 xl:h-14 xl:w-14 rounded-xl':
                          sessionStatus === 'draft',
                        'h-18 w-18 rounded-3xl': sessionStatus !== 'draft',
                        'bg-teal-700': prompt.id === currentTrial.prompt_id,
                        'bg-teal-100': prompt.id !== currentTrial.prompt_id
                      }"
                      @click="onChoosePrompt(prompt)"
                    >
                      <BlTextTitle3
                        :text="prompt.abbreviation"
                        :class="{
                          'text-pure-white':
                            prompt.id === currentTrial.prompt_id,
                          'text-teal-700': prompt.id !== currentTrial.prompt_id
                        }"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="pageCount > 1"
              class="flex items-center justify-center h-3 gap-2"
            >
              <div
                v-for="(n, idx) in pageCount"
                :key="idx"
                :class="{
                  'bg-slate-700': n === page,
                  'bg-slate-400': n !== page
                }"
                class="w-3 h-3 transition-all duration-300 rounded-full cursor-pointer"
                @click="onChangePage(n)"
              ></div>
            </div>
          </div>

          <!-- select a next task -->
          <div
            v-if="display === 'select-next-task'"
            class="flex flex-col items-center content-center justify-center h-full"
            :class="{
              'gap-4': !isCollapsed,
              'gap-2': isCollapsed
            }"
          >
            <div
              v-if="
                Object.keys(resultsState).length > 1 || currentTrial.prompt_id
              "
              class="flex items-center text-center"
              :class="{
                'flex-col gap-1': !isCollapsed,
                'flex-row gap-2': isCollapsed
              }"
            >
              <BlTextSubtitle3
                :text="currentTrialData.prompt.name"
                class="text-slate-700"
              />
              <BlTextTitle1
                v-if="!isCollapsed"
                :text="`${currentTrialData.prompt.score}%`"
                class="text-teal-700"
              />
              <BlTextSubtitle3
                v-if="isCollapsed"
                :text="`${currentTrialData.prompt.score}%`"
                class="text-teal-700"
              />
              <BlInputButton
                v-if="!isCollapsed"
                type="plain"
                dense
                label="Change"
                @clicked="display = 'select-prompt'"
              />
            </div>
            <div class="flex flex-col items-center gap-1 px-2">
              <div v-if="!isCollapsed" class="text-center">
                <BlTextBody2
                  v-if="
                    Object.keys(resultsState).length > 1 ||
                      currentTrial.prompt_id
                  "
                  text="Next"
                />
                <BlTextBody2 v-else text="Select a task to start" />
              </div>
              <div
                :class="{ 'overflow-x-auto scrollbar-lg pb-2': isCollapsed }"
                :style="isCollapsed ? { maxWidth: 'calc(100vw - 6rem)' } : {}"
              >
                <div
                  class="flex items-center gap-2"
                  :class="{ 'justify-center flex-wrap': !isCollapsed }"
                >
                  <BlInputButton
                    v-for="taskCode in ratioScores"
                    :key="taskCode.id"
                    :type="
                      taskCode.id === nextTask.id ? 'teal' : 'outlined-teal'
                    "
                    dense
                    :label="`${taskCode.code} | ${taskCode.count + 1}`"
                    @clicked="nextTask = taskCode"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- select a problem behavior -->
        <div
          v-if="isOpenProblemBehavior"
          class="flex flex-col items-center content-center justify-center w-full h-full"
          :class="{
            'gap-4': isCollapsed,
            'gap-2': !isCollapsed
          }"
        >
          <div
            class="flex flex-wrap items-start content-center justify-center w-full gap-x-2 gap-y-3"
            :class="{ '-translate-y-1 scale-75': isCollapsed }"
          >
            <div
              v-for="(problemBehavior, idx) in SBTProblemBehaviors"
              :key="idx"
            >
              <div
                class="relative flex items-center justify-center flex-shrink-0 cursor-pointer hover-darker"
                :class="{
                  'h-12 w-12 xl:h-14 xl:w-14 rounded-xl':
                    sessionStatus === 'draft',
                  'h-18 w-18 rounded-3xl': sessionStatus !== 'draft',
                  'bg-tomato-700':
                    problemBehavior.id ===
                    currentTrial.target_problem_behavior_id,
                  'bg-tomato-100':
                    problemBehavior.id !==
                    currentTrial.target_problem_behavior_id
                }"
                @click="onChooseProblemBehavior(problemBehavior)"
              >
                <BlTextTitle3
                  :text="problemBehavior.code"
                  :class="{
                    'text-pure-white':
                      problemBehavior.id ===
                      currentTrial.target_problem_behavior_id,
                    'text-tomato-700':
                      problemBehavior.id !==
                      currentTrial.target_problem_behavior_id
                  }"
                />
              </div>
            </div>
          </div>
          <div>
            <BlTextBody2
              :text="currentTrialData.problem_behavior.code_definition"
            />
          </div>
        </div>
      </div>

      <!-- view trial history -->
      <div
        v-if="isOpenTrialHistory && !isOpenEditTrial"
        class="flex flex-col overflow-y-auto"
        :style="{ maxHeight: 'calc(100vh - 32rem)' }"
      >
        <div v-for="key in Object.keys(resultsState)" :key="key">
          <div
            v-if="resultsState[key].prompt_id"
            class="flex items-center justify-between px-2 py-3 border-b border-slate-300"
          >
            <div class="flex items-center gap-2">
              <div class="flex-shrink-0 w-6">
                <BlTextBody2 :text="`${key}.`" />
              </div>
              <div>
                <BlTextSubtitle3
                  :text="getCode(resultsState[key].target_task_id, 'task_code')"
                  class="text-slate-700"
                />
              </div>
            </div>
            <div class="flex items-center gap-2">
              <div
                v-if="resultsState[key].target_problem_behavior_id"
                class="flex items-center justify-center w-6 h-6 rounded bg-tomato-700"
              >
                <BlTextSubtitle3
                  :text="
                    getCode(
                      resultsState[key].target_problem_behavior_id,
                      'problem_behavior'
                    )
                  "
                  class="text-pure-white"
                />
              </div>
              <div
                class="flex items-center justify-center w-6 h-6 bg-teal-700 rounded"
              >
                <BlTextSubtitle3
                  :text="getCode(resultsState[key].prompt_id, 'prompt')"
                  class="text-pure-white"
                />
              </div>
              <BlIcon
                v-if="sessionStatus !== 'completed'"
                icon="edit"
                color="slate-8"
                clickable
                class="w-5 h-5"
                @clicked="onOpenEditTrial(key)"
              />
            </div>
          </div>
          <div
            v-else-if="Object.keys(resultsState).length === 1"
            class="py-4 text-center"
          >
            <BlTextBody2 text="No result recorded yet." />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!isCollapsed || display === 'select-next-task'"
      class="sticky w-full bg-pure-white"
      :class="{
        'bottom-0': sessionStatus !== 'draft',
        '-bottom-3 pb-3': sessionStatus === 'draft'
      }"
    >
      <div v-if="isOpenProblemBehavior" class="flex-grow">
        <BlInputButton
          type="outlined"
          class="w-full"
          label="Back to result"
          @clicked="isOpenProblemBehavior = false"
        />
      </div>
      <div
        v-else-if="isOpenTrialHistory && !isOpenEditTrial"
        class="flex-grow "
      >
        <BlInputButton
          type="outlined"
          class="w-full"
          label="Back"
          @clicked="onCloseTrialHistory"
        />
      </div>
      <div v-else class="flex items-center justify-between flex-shrink-0 gap-3">
        <BlInputButton
          v-if="!isOpenEditTrial && !isCollapsed"
          type="outlined"
          icon="menu"
          class="flex-shrink-0"
          @clicked="onOpenTrialHistory"
        />
        <BlInputButton
          v-if="isOpenEditTrial && !isCollapsed"
          type="outlined"
          icon="menu"
          class="flex-shrink-0"
          @clicked="onCloseEditTrial"
        />
        <div
          v-if="
            nextTask.code && display === 'select-next-task' && !isOpenEditTrial
          "
          class="flex-grow"
        >
          <BlInputButton
            v-if="
              Object.keys(resultsState).length > 1 || currentTrial.prompt_id
            "
            type="teal"
            class="w-full"
            :label="`Next: ${nextTask.code} | ${nextTask.count + 1}`"
            :loading="submitLoading"
            @clicked="onNextTrial"
          />
          <BlInputButton
            v-else
            type="teal"
            class="w-full"
            :label="`Start trial: ${nextTask.code}`"
            @clicked="onTakeNextTrial({ isNew: false })"
          />
        </div>
        <div v-if="isOpenEditTrial" class="flex-grow">
          <BlInputButton
            type="teal"
            class="w-full"
            :label="`Update`"
            :loading="submitLoading"
            @clicked="onSaveEditTrial"
          />
        </div>
        <div
          v-if="Object.keys(resultsState).length > 1 || currentTrial.prompt_id"
          class="flex items-center justify-center w-10 h-10 rounded cursor-pointer hover-darker"
          :class="{
            'bg-tomato-200': !currentTrial.target_problem_behavior_id,
            'bg-tomato-700': currentTrial.target_problem_behavior_id
          }"
          @click="isOpenProblemBehavior = true"
        >
          <BlTextSubtitle3
            :text="currentTrialData.problem_behavior.code"
            :class="{
              'text-tomato-700': !currentTrial.target_problem_behavior_id,
              'text-pure-white': currentTrial.target_problem_behavior_id
            }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import BlTextTitle1 from "../../BlTextTitle1.vue";
import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "MeasurementCardSkillBasedTreatment",
  components: {
    BlTextTitle1,
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputButton
  },
  props: {
    measurement: { type: Object, required: true },
    target: { type: Object, default: () => {} },
    measurementResults: { type: Object, default: () => {} },
    sessionStatus: { type: String, required: true },
    isCollapsed: { type: Boolean, default: false }
    // @toggle-saved
  },
  data() {
    return {
      isSaved: true,
      //
      page: 1,
      submitLoading: false,
      display: "select-prompt", // select-prompt, select-next-task
      //
      activeTrial: {
        key: "0",
        target_task_id: 0,
        prompt_id: 0,
        target_problem_behavior_id: null // optional
      },
      editTrial: {
        key: "0",
        target_task_id: 0,
        prompt_id: 0,
        target_problem_behavior_id: null // optional
      },
      nextTask: {},
      ratioScores: [],
      resultsState: {},
      //
      isOpenProblemBehavior: false,
      //
      isOpenTrialHistory: false,
      isOpenEditTrial: false,
      // state prompts task_codes problem_behaviors from measurement.target
      SBTPrompts: [],
      SBTTaskCodes: [],
      SBTProblemBehaviors: []
    };
  },
  computed: {
    ...mapGetters(["viewMode"]),
    //
    currentTrial: {
      get() {
        if (this.isOpenEditTrial) {
          return this.editTrial;
        } else {
          return this.activeTrial;
        }
      },
      set(val) {
        if (this.isOpenEditTrial) {
          this.editTrial = val;
        } else {
          this.activeTrial = val;
        }
      }
    },
    currentTrialData() {
      return this.getTrial(this.currentTrial);
    },
    //
    perPage() {
      return this.isCollapsed ? 3 : 9;
    },
    pageCount() {
      const prompts = [...this.SBTPrompts] || [];
      const boxes = prompts.length;
      return Math.ceil(boxes / this.perPage);
    },
    promptBoxesPages() {
      const prompts =
        [...this.SBTPrompts].sort((a, b) => b.score - a.score) || [];
      const res = [];
      for (let idx = 1; idx <= this.pageCount; idx++) {
        const start = (idx - 1) * this.perPage;
        const end = idx * this.perPage;
        const arr = [...prompts.slice(start, end)];
        res.push(arr);
      }
      return res;
    }
  },
  watch: {
    isSaved(val) {
      this.$emit("toggle-saved", val);
    },
    measurementResults: {
      deep: true,
      handler(val) {
        this.resultsState = val;
        this.generateRatioScores();
      }
    }
  },
  async mounted() {
    const tasks = this.target.target_tasks || [];
    const problems = this.target.target_problem_behaviors || [];
    this.SBTPrompts = this.target.prompts || [];
    this.SBTTaskCodes = tasks.sort((a, b) => a.position - b.position);
    this.SBTProblemBehaviors = problems.sort((a, b) => a.position - b.position);
    this.resultsState = this.measurementResults;

    this.generateRatioScores();

    const results = Object.keys(this.resultsState).map(key => ({
      ...this.resultsState[key],
      key
    }));

    if (results.length) {
      const length = results.length;
      const lastTrial = results[length - 1];

      if (!lastTrial.prompt_id) {
        this.currentTrial = lastTrial;
      } else {
        const index = this.SBTTaskCodes.findIndex(
          i => i.id === lastTrial.target_task_id
        );
        let nextTaskId = 0;
        if (index > -1 && index + 1 <= this.SBTTaskCodes.length - 1) {
          nextTaskId = this.SBTTaskCodes[index + 1].id;
        } else if (this.SBTTaskCodes.length) {
          nextTaskId = this.SBTTaskCodes[0].id;
        }
        const newTrial = {
          key: Number(lastTrial.key) + 1 + "",
          target_task_id: nextTaskId,
          prompt_id: 0,
          target_problem_behavior_id: null // optional
        };
        this.resultsState[newTrial.key] = {
          key: newTrial.key,
          target_task_id: newTrial.target_task_id,
          prompt_id: newTrial.prompt_id,
          target_problem_behavior_id: newTrial.target_problem_behavior_id // optional
        };
        this.currentTrial = newTrial;
      }
    } else {
      const newTrial = {
        key: "1",
        target_task_id: 0,
        prompt_id: 0,
        target_problem_behavior_id: null // optional
      };
      if (this.SBTTaskCodes.length) {
        newTrial.target_task_id = this.SBTTaskCodes[0].id;
      }
      this.resultsState[newTrial.key] = {
        key: newTrial.key,
        target_task_id: newTrial.target_task_id,
        prompt_id: newTrial.prompt_id,
        target_problem_behavior_id: newTrial.target_problem_behavior_id // optional
      };
      this.currentTrial = newTrial;
    }
  },
  methods: {
    ...mapActions(["updateMeasurementResults"]),
    onScroll(e) {
      const offsetWidth = e.currentTarget.offsetWidth;
      const scrollLeft = e.currentTarget.scrollLeft;
      const current = Math.floor(scrollLeft / offsetWidth) + 1;
      this.page = current;
    },
    onChangePage(num) {
      const id = `${this.measurement.id}-sbt-prompt-boxes-${num}`;
      const boxes = document.getElementById(`${id}`);
      if (boxes) {
        boxes.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    },
    getCode(id, data) {
      if (data === "task_code") {
        const found = this.SBTTaskCodes.find(i => i.id === id);
        return found ? found.code : "";
      }
      if (data === "prompt") {
        const found = this.SBTPrompts.find(i => i.id === id);
        return found ? found.abbreviation : "";
      }
      if (data === "problem_behavior") {
        const found = this.SBTProblemBehaviors.find(i => i.id === id);
        return found ? found.code : "";
      }
      return "-";
    },
    //
    getTrial(trial) {
      const defaultTaskCode = this.SBTTaskCodes.length
        ? this.SBTTaskCodes[0]
        : { code: "" };
      const taskCode =
        this.SBTTaskCodes.find(i => i.id === trial.target_task_id) ||
        defaultTaskCode;
      const prompt = this.SBTPrompts.find(i => i.id === trial.prompt_id) || {
        name: "",
        score: ""
      };
      const problemBehavior = this.SBTProblemBehaviors.find(
        i => i.id === trial.target_problem_behavior_id
      ) || { code: "R", code_definition: "" };

      let average = 0;
      const ratioScore = this.ratioScores.find(
        i => i.id === trial.target_task_id
      );
      if (ratioScore) {
        average = ratioScore.average;
      }
      return {
        ...trial,
        task_code: taskCode,
        prompt,
        problem_behavior: problemBehavior || { code: "R", code_definition: "" },
        average
      };
    },
    generateRatioScores() {
      /**
       * percentage: average all task
       * green ratio: ratio of current task count from the most task count
       * red badge: any problem_behavior(?) yes: put the badge
       */

      let maxRatio = 0;
      const results = Object.keys(this.resultsState).map(key => ({
        ...this.resultsState[key],
        key
      }));

      const taskCodes = [...this.SBTTaskCodes].map(i => {
        const ratios = results.filter(r => r.target_task_id === i.id);
        const listOfScore = ratios
          .map(r => {
            const prompt = this.SBTPrompts.find(p => p.id === r.prompt_id);
            return prompt ? prompt.score : null;
          })
          .filter(r => r !== null);
        const sum = listOfScore.reduce((sum, i) => (sum += i), 0);
        const average = Math.round(sum / listOfScore.length || 0);

        if (listOfScore.length > maxRatio) maxRatio = listOfScore.length;
        return { ...i, sum, average, count: listOfScore.length, ratios };
      });

      const assignedMaxRatio = taskCodes.map(i => {
        return { ...i, max_ratio: maxRatio };
      });
      this.ratioScores = [...assignedMaxRatio];
    },
    onOpenTrialHistory() {
      this.isOpenTrialHistory = true;
    },
    onCloseTrialHistory() {
      this.isOpenTrialHistory = false;

      const results = Object.keys(this.resultsState).map(key => ({
        ...this.resultsState[key],
        key
      }));

      const index = results.findIndex(i => i.key === this.currentTrial.key);
      if (index > -1) {
        this.currentTrial = results[index];
      }

      if (
        Object.keys(this.resultsState).length > 0 &&
        this.resultsState[this.currentTrial.key] &&
        this.resultsState[this.currentTrial.key].prompt_id
      ) {
        this.display = "select-next-task";
      } else {
        this.display = "select-prompt";
      }
    },
    //
    async onSaveCurrentTrial() {
      if (this.sessionStatus !== "ongoing") return { success: false };
      if (this.submitLoading) return { success: false };

      let results = Object.keys(this.resultsState).map(key => {
        return { ...this.resultsState[key], key };
      });

      const index = results.findIndex(i => i.key === this.currentTrial.key);
      if (index > -1) {
        results[index] = this.currentTrial;
      } else {
        results.push(this.currentTrial);
      }

      results = results
        .filter(i => i.prompt_id && i.target_task_id)
        .map((i, idx) => ({ ...i, key: idx + 1 }));

      const params = {
        id: this.measurement.id,
        results: {}
      };

      for (let idx = 0; idx < results.length; idx++) {
        params.results[idx + 1] = results[idx];
      }

      this.submitLoading = true;
      const { success, data } = await this.updateMeasurementResults(params);
      this.submitLoading = false;
      if (!success) return { success: false };

      // complete saved
      this.isSaved = true;
      this.resultsState = data.results;
      return { success: true };
    },
    //
    async onChoosePrompt(prompt) {
      if (this.sessionStatus !== "ongoing") return;
      const newTrial = {
        ...this.currentTrial,
        prompt_id: prompt.id
      };

      this.isSaved = false;
      this.currentTrial = newTrial;

      if (this.isOpenEditTrial) {
        return;
      }

      await this.onSaveCurrentTrial();

      const taskIndex = this.ratioScores.findIndex(
        i => i.id === newTrial.target_task_id
      );
      if (taskIndex > -1 && taskIndex + 1 <= this.ratioScores.length - 1) {
        this.nextTask = this.ratioScores[taskIndex + 1];
      } else {
        this.nextTask = this.ratioScores[0];
      }
      this.display = "select-next-task";
    },
    async onChooseProblemBehavior(problemBehavior) {
      if (this.sessionStatus !== "ongoing") return;
      let newId = problemBehavior.id;
      if (this.currentTrial.target_problem_behavior_id === newId) {
        newId = null;
      }

      const newTrial = {
        ...this.currentTrial,
        target_problem_behavior_id: newId
      };

      this.isSaved = false;
      this.currentTrial = newTrial;

      if (this.isOpenEditTrial) {
        return;
      }

      await this.onSaveCurrentTrial();
    },
    onUndoTrial() {
      if (this.sessionStatus !== "ongoing") return;
      const results = Object.keys(this.resultsState).map(key => ({
        ...this.resultsState[key],
        key
      }));

      let newTrial = {
        key: "1",
        target_task_id: 0,
        prompt_id: 0,
        target_problem_behavior_id: null // optional
      };
      if (results.length) {
        // take last trial for current trial
        newTrial = {
          ...results[results.length - 1],
          key: results.length
        };
      }
      // change active trial
      this.currentTrial = newTrial;

      // change next task
      const index = this.ratioScores.findIndex(
        i => i.id === newTrial.target_task_id
      );
      if (index > -1 && index + 1 <= this.ratioScores.length - 1) {
        this.nextTask = this.ratioScores[index + 1];
      } else {
        this.nextTask = this.ratioScores[0];
      }

      this.display = "select-next-task";

      // re-select next task
      this.isSaved = false;
    },
    onTakeNextTrial(payload = { isNew: false }) {
      if (this.sessionStatus !== "ongoing") return;
      const newKey = payload.isNew
        ? Number(this.currentTrial.key) + 1 + ""
        : this.currentTrial.key;
      const newTrial = {
        key: newKey,
        target_task_id: this.nextTask.id,
        prompt_id: 0,
        target_problem_behavior_id: null // optional
      };
      this.resultsState[newTrial.key] = {
        key: newTrial.key,
        target_task_id: newTrial.target_task_id,
        prompt_id: newTrial.prompt_id,
        target_problem_behavior_id: newTrial.target_problem_behavior_id // optional
      };
      this.display = "select-prompt";
      this.currentTrial = newTrial;
      this.nextTask = {};
      this.generateRatioScores();
    },
    // ongoing
    async onNextTrial() {
      const { success } = await this.onSaveCurrentTrial();
      if (!success) return;
      this.onTakeNextTrial({ isNew: true });
    },
    //
    onOpenEditTrial(key) {
      let results = Object.keys(this.resultsState).map(key => {
        return { ...this.resultsState[key], key };
      });
      const index = results.findIndex(i => i.key === key);
      if (index > -1) {
        this.isOpenEditTrial = true;
        this.currentTrial = results[index];
        this.display = "select-prompt";

        // open edit trial
        this.isSaved = false;
      }
    },
    onCloseEditTrial() {
      // cancel edit trial
      this.isSaved = true;
      this.isOpenEditTrial = false;
    },
    async onSaveEditTrial() {
      const { success } = await this.onSaveCurrentTrial();
      if (!success) return;
      this.isOpenEditTrial = false;
      this.generateRatioScores();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/card/SkillBasedTreatment.vue